/**
 * Where a user lands when they open the app.
 *
 * Phase 3 Package 7 (.claude/specs/module-ui-rollout.md AC16).
 *
 * The point of the launcher is that switching modules is deliberate rather than
 * forced. This is the other half: someone who lives in one module all day should
 * be able to start there, without everyone else being made to choose on every
 * login.
 *
 * Default is "core", so nothing changes for anyone who never touches this.
 *
 * Stored per user in localStorage rather than on the server. It is a device
 * preference — the same person on a shared machine should not inherit someone
 * else's landing page, and a laptop and a phone can reasonably differ. Keying by
 * user id is what stops the leak on a shared browser.
 */
import { ModuleKey, MODULE_KEYS, CORE_LABEL } from "./moduleMetadata";

export type LandingPreference = "core" | "last_used" | "ask";

export const LANDING_PREFERENCES: { value: LandingPreference; label: string }[] = [
  // Stored value stays "core" — it is a persisted localStorage key, not a label.
  { value: "core", label: CORE_LABEL },
  { value: "last_used", label: "Last module I used" },
  { value: "ask", label: "Ask me every time" },
];

const PREF_KEY = (userId: number | string) => `hrxai.landing.pref.${userId}`;
const LAST_KEY = (userId: number | string) => `hrxai.landing.last.${userId}`;

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private browsing and some embedded webviews throw on access rather than
    // returning null. A missing preference is not worth breaking the app over.
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* preference simply does not persist */
  }
}

export function getLandingPreference(userId: number | string | undefined): LandingPreference {
  if (userId == null) return "core";
  const raw = safeGet(PREF_KEY(userId));
  return raw === "last_used" || raw === "ask" ? raw : "core";
}

export function setLandingPreference(userId: number | string | undefined, pref: LandingPreference): void {
  if (userId == null) return;
  safeSet(PREF_KEY(userId), pref);
}

/** Remember the module a user is in, so "last used" has something to point at. */
export function rememberLastModule(userId: number | string | undefined, module: ModuleKey | null): void {
  if (userId == null) return;
  safeSet(LAST_KEY(userId), module ?? "core");
}

/**
 * The module to open on landing, or null for Core.
 *
 * Returns null unless the preference is "last_used" AND a real module was
 * recorded AND the tenant still owns it. A module can be revoked between
 * sessions, and landing straight on an upsell page would be a hostile way to
 * open the app.
 */
export function resolveLandingModule(
  userId: number | string | undefined,
  ownedModules: Partial<Record<ModuleKey, boolean>> | undefined,
): ModuleKey | null {
  if (userId == null) return null;
  if (getLandingPreference(userId) !== "last_used") return null;

  const last = safeGet(LAST_KEY(userId));
  if (!last || last === "core") return null;
  if (!(MODULE_KEYS as readonly string[]).includes(last)) return null;

  const key = last as ModuleKey;
  return ownedModules?.[key] === true ? key : null;
}
